import React, { forwardRef } from 'react';
import './Input.css';

/**
 * Input Component
 *
 * @param {string} size - 'sm' | 'md' | 'lg'
 * @param {string} label - Input label
 * @param {string} error - Error message
 * @param {string} hint - Hint text
 * @param {ReactNode} leftIcon - Icon on the left
 * @param {ReactNode} rightIcon - Icon on the right
 */
const Input = forwardRef(({
  type = 'text',
  size = 'md',
  label,
  error,
  hint,
  leftIcon,
  rightIcon,
  fullWidth = true,
  className = '',
  disabled = false,
  required = false,
  id,
  ...props
}, ref) => {
  const inputId = id || `input-${Math.random().toString(36).substr(2, 9)}`;

  const containerClassNames = [
    'oz-input-container',
    fullWidth && 'oz-input-container--full-width',
    className
  ].filter(Boolean).join(' ');

  const inputWrapperClassNames = [
    'oz-input-wrapper',
    `oz-input-wrapper--${size}`,
    error && 'oz-input-wrapper--error',
    disabled && 'oz-input-wrapper--disabled',
    leftIcon && 'oz-input-wrapper--has-left-icon',
    rightIcon && 'oz-input-wrapper--has-right-icon',
  ].filter(Boolean).join(' ');

  return (
    <div className={containerClassNames}>
      {label && (
        <label htmlFor={inputId} className="oz-input-label">
          {label}
          {required && <span className="oz-input-required">*</span>}
        </label>
      )}
      <div className={inputWrapperClassNames}>
        {leftIcon && <span className="oz-input-icon oz-input-icon--left">{leftIcon}</span>}
        <input
          ref={ref}
          id={inputId}
          type={type}
          className="oz-input"
          disabled={disabled}
          required={required}
          {...props}
        />
        {rightIcon && <span className="oz-input-icon oz-input-icon--right">{rightIcon}</span>}
      </div>
      {error && <span className="oz-input-error">{error}</span>}
      {hint && !error && <span className="oz-input-hint">{hint}</span>}
    </div>
  );
});

Input.displayName = 'Input';

// Textarea Component
export const Textarea = forwardRef(({
  label,
  error,
  hint,
  fullWidth = true,
  className = '',
  disabled = false,
  required = false,
  rows = 4,
  id,
  ...props
}, ref) => {
  const inputId = id || `textarea-${Math.random().toString(36).substr(2, 9)}`;

  const containerClassNames = [
    'oz-input-container',
    fullWidth && 'oz-input-container--full-width',
    className
  ].filter(Boolean).join(' ');

  const textareaClassNames = [
    'oz-textarea',
    error && 'oz-textarea--error',
    disabled && 'oz-textarea--disabled',
  ].filter(Boolean).join(' ');

  return (
    <div className={containerClassNames}>
      {label && (
        <label htmlFor={inputId} className="oz-input-label">
          {label}
          {required && <span className="oz-input-required">*</span>}
        </label>
      )}
      <textarea
        ref={ref}
        id={inputId}
        className={textareaClassNames}
        disabled={disabled}
        required={required}
        rows={rows}
        {...props}
      />
      {error && <span className="oz-input-error">{error}</span>}
      {hint && !error && <span className="oz-input-hint">{hint}</span>}
    </div>
  );
});

Textarea.displayName = 'Textarea';

export default Input;
